import React, { useCallback, useEffect } from 'react';

import { Box, Flex, SlideFade } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';
import { checkAIReviewCommentsEnabled } from 'utils/helpers/featureFlags';
import { useClearMutationCache } from 'utils/helpers/reactQueryHelpers';
import uuidv4 from 'utils/uuidv4';

import { Cross, Details } from 'assets/assetsV1/iconComponents';
import { MeshMavenWithHording } from 'assets/assetsV1/iconComponents/MeshMavenWithHording';

import { noOp } from 'constants/helper';
import { sendUsedMeshMavenGeneratedContentEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { Button } from 'uiToolkitV1/Button';
import BetaTag from 'uiToolkitV1/Tag/BetaTag';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { PLACE_HOLDER_WITHOUT_AI, PLACE_HOLDER_WITH_AI } from '../MLCommentTextGenerationComp/constants/placeHolders';
import AnalyzeText from './AnalyzeText';
import RephraseText from './RephraseText';
import { useCustomerFeedbackMutation } from './dataHooks/customerFeedback';
import {
  resetState,
  setAddRevision,
  setGroupId,
  setRevisionNumber,
  useRevisions,
  useSelectedRevision,
} from './slice/meshMavenSlice';

const Header = ({ onClose }) => (
  <Flex justify="space-between" alignItems="center" px={2}>
    <Flex flexGrow={1} justify="flex-start" alignItems="center" px={2}>
      <MeshMavenWithHording boxSize="32px" />
      <TextBodyMedium bold mx={2}>
        Maven
      </TextBodyMedium>
      <BetaTag />
    </Flex>
    <Box onClick={onClose} p={2} cursor="pointer">
      <Cross boxSize="24px" color="icon.g2" />
    </Box>
  </Flex>
);

export interface MLCommentTextGenerationCompProps {
  reviewId: string;
  questionId: string;
  reviewerId: string;
  revieweeId: string;
  textHtml?: string;
  plainText?: string;
  onChange?: ({ feedbackText, feedbackTextHtml }: { feedbackText: string; feedbackTextHtml: string }) => void;
  onClose?: () => void;
  revieweeDisplayName?: string;
}

const MLCommentTextGenerationCompV1 = ({
  textHtml = '',
  plainText = '',
  onChange = noOp,
  onClose = noOp,
  reviewId,
  questionId,
  reviewerId,
  revieweeId,
  revieweeDisplayName = '',
}: MLCommentTextGenerationCompProps) => {
  const isSelfReview = reviewerId === revieweeId;
  const toast = useToast();
  const dispatch = useDispatch();

  const { employeeId, providerId } = useReviewFormParams();
  const queryClient = useQueryClient();
  const sidebarData = queryClient.getQueryData([REACT_QUERY_KEYS.ReviewFormSidebar, employeeId, reviewId, providerId]);
  const { reviewInfo } = sidebarData ?? {};
  const { providerType } = reviewInfo ?? {};

  const revisions = useRevisions();
  const revision = useSelectedRevision();

  const { mutate: submitCustomerFeedback } = useCustomerFeedbackMutation();
  const clearMutationCache = useClearMutationCache();

  const handleFeedback = () => {
    submitCustomerFeedback({ userAction: 'USE' });
  };

  const onChangeHandler = useCallback(
    // second parameter takes default value of first in case of paste where this is called with only one parameter
    (feedbackTextHtml, feedbackText = feedbackTextHtml) => {
      onChange({
        feedbackText,
        feedbackTextHtml,
      });
    },
    [onChange, textHtml]
  );

  const onCloseWithToast = (isCopyAction = false) => {
    if (isCopyAction) {
      toast({
        title: 'Your draft has been copied to comments',
        status: 'success',
      });
    }
    onClose();
    clearMutationCache([REACT_QUERY_KEYS.reviewsGenAi.analyzeText]);
    clearMutationCache([REACT_QUERY_KEYS.reviewsGenAi.customerFeedback]);
    clearMutationCache([REACT_QUERY_KEYS.reviewsGenAi.rephraseText]);
    dispatch(resetState());
  };

  // to reset state when component is unmounted
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onCloseWithToast, []);

  useEffect(() => {
    dispatch(setGroupId({ groupId: uuidv4() }));
    dispatch(
      setAddRevision({
        revision: {
          requestId: '',
          originalState: plainText,
          originalStateHtml: textHtml,
          updatedState: plainText,
          updatedStateHtml: textHtml,
          isAIFeedbackProvided: false,
          isReanalyzeEnabled: false,
          isTextLengthValid: plainText.length >= 15,
        },
      })
    );
    dispatch(setRevisionNumber({ revisionNumber: revisions.length }));
  }, []);

  const reviewCommentPlaceholder = checkAIReviewCommentsEnabled() ? PLACE_HOLDER_WITH_AI : PLACE_HOLDER_WITHOUT_AI;

  return (
    <SlideFade in offsetX="500px">
      <Flex
        borderRadius="8px"
        flexGrow={1}
        p="1px"
        bg="linear-gradient(90deg, rgba(0, 142, 240, 0.50) 1.83%, rgba(236, 59, 100, 0.50) 34.86%, rgba(255, 194, 0, 0.50) 65.54%, rgba(92, 196, 97, 0.50) 100%)"
      >
        <Flex flexDir="column" py="2" borderRadius="8px" flexGrow={1} bg="white">
          <Header onClose={() => onCloseWithToast(false)} />
          <Flex p="4px 12px 12px" flexGrow={1} borderRadius="8px">
            <Flex
              flexGrow={1}
              p={1}
              borderRadius="8px"
              bg="linear-gradient(90deg, rgba(0, 142, 240, 0.08) 1.83%, rgba(236, 59, 100, 0.08) 34.86%, rgba(255, 194, 0, 0.08) 65.54%, rgba(92, 196, 97, 0.08) 100%)"
              flexDir="column"
            >
              <Flex
                flexGrow={1}
                alignItems="stretch"
                bgColor="white"
                p={2}
                borderRadius="8px 8px 0 0"
                gap={2}
                flexDirection={{ base: 'column', desktop: 'row' }}
              >
                {/* Analyzing ------------------*/}
                <AnalyzeText
                  isSelfReview={isSelfReview}
                  questionId={questionId}
                  reviewId={reviewId}
                  revieweeDisplayName={revieweeDisplayName}
                  revieweeId={revieweeId}
                />
                {/* Rephrasing ----------------*/}
                <RephraseText
                  reviewCommentPlaceholder={reviewCommentPlaceholder}
                  reviewId={reviewId}
                  revieweeId={revieweeId}
                  questionId={questionId}
                />
              </Flex>
              <Flex
                bgColor="neutral.F5F7F9"
                p="8px 16px"
                justifyContent="space-between"
                boxShadow="0px -1px 0px 0px #CFD7DF"
              >
                <Button variant="tertiary" onClick={() => onCloseWithToast(false)}>
                  Cancel
                </Button>
                <Button
                  StartIcon={Details}
                  startIconProps={{ fill: 'none' }}
                  onClick={() => {
                    onCloseWithToast(true);
                    onChangeHandler(revision.updatedStateHtml, revision.updatedState);
                    handleFeedback();
                    sendUsedMeshMavenGeneratedContentEvent({
                      eventProperties: {
                        source: providerType,
                      },
                    });
                  }}
                >
                  Use this content
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </SlideFade>
  );
};

export default MLCommentTextGenerationCompV1;
